"use client";

import { useSyncExternalStore } from "react";

const STORAGE_KEY = "aiarva-platform-auth";

type AuthSnapshot = {
  token: string | null;
  user: any | null;
};

const EMPTY: AuthSnapshot = { token: null, user: null };

const listeners = new Set<() => void>();
let snapshot: AuthSnapshot = EMPTY;
let initialized = false;

function loadFromStorage(): AuthSnapshot {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    const state = parsed?.state ?? parsed;
    return {
      token: state?.token ?? null,
      user: state?.user ?? null,
    };
  } catch {
    return EMPTY;
  }
}

function writeToStorage(s: AuthSnapshot) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ state: s, version: 0 }),
    );
  } catch {
    // ignore
  }
}

function ensureInit() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  snapshot = loadFromStorage();
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      snapshot = loadFromStorage();
      listeners.forEach((l) => l());
    }
  });
}

function subscribe(cb: () => void) {
  ensureInit();
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): AuthSnapshot {
  ensureInit();
  return snapshot;
}

function getServerSnapshot(): AuthSnapshot {
  return EMPTY;
}

function setAuth(token: string, user: any) {
  snapshot = { token, user };
  writeToStorage(snapshot);
  listeners.forEach((l) => l());
}

function clearAuth() {
  snapshot = EMPTY;
  writeToStorage(snapshot);
  listeners.forEach((l) => l());
}

export function useAuthStore() {
  const current = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  return {
    token: current.token,
    user: current.user,
    setAuth,
    clearAuth,
  };
}
